import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SessionQuery } from '@state/session/session.query';
import { AesRequestModel, USER_PIN_PARAM } from '@core/models/AesRequest.model';
import { ClinicQuery } from '@state/clinic/clinic.query';
import { AlertController } from '@ionic/angular';
import { LibraryService } from '@core/services/library/library.service';
import { ToastService } from '../toast/toast.service';
import { from, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    constructor(
        private alertController: AlertController,
        private clinicQuery: ClinicQuery,
        private http: HttpClient,
        private libraryService: LibraryService,
        private sessionQuery: SessionQuery,
        private toastService: ToastService
    ) {
    }

    async callApi(form: AesRequestModel): Promise<string> {
        const url = this.sessionQuery.getValue().aesUrl;

        if (this.hasUserRequiredPin() && this.hasSecRequiredPin(form.Form)) {
            try {
                const pin = await this.createAlert();
                form.Data.push({
                    Param: USER_PIN_PARAM,
                    Value: pin,
                    AESIndex: ''
                });
            } catch (e) {
                if (e instanceof IncorrectPinValueError) {
                    this.toastService.presentErrorToast(this.libraryService.getLabel('lbl_incorrect_pin_value'));
                }

                throw e;
            }
        }

        return this.http.post(url, form, { responseType: 'text' }).toPromise();
    }

    callApiObservable(form: AesRequestModel): Observable<string> {
        const url = this.sessionQuery.getValue().aesUrl;

        if (this.hasUserRequiredPin() && this.hasSecRequiredPin(form.Form)) {
            return from(this.createAlert())
                .pipe(
                    catchError(e => {
                        if (e instanceof IncorrectPinValueError) {
                            this.toastService.presentErrorToast(this.libraryService.getLabel('lbl_incorrect_pin_value'));
                        }

                        throw e;
                    }),
                    tap((pin) => {
                        form.Data.push({
                            Param: USER_PIN_PARAM,
                            Value: pin,
                            AESIndex: ''
                        });
                    }),
                    switchMap(() => this.http.post(url, form, { responseType: 'text' }))
                );
        }

        return this.http.post(url, form, { responseType: 'text' });
    }

    private hasUserRequiredPin(): boolean {
        return this.sessionQuery.getValue().userRequirePin;
    }

    private hasSecRequiredPin(sec: string): boolean {
        const clinic = this.clinicQuery.getActive();

        if (!clinic || !clinic.pinRequiredSecs) {
            return true;
        }

        return clinic.pinRequiredSecs.some(protectedSec => sec.includes(protectedSec));
    }

    private async createAlert(): Promise<string> {
        const alert = await this.alertController.create({
            message: this.libraryService.getLabel('lbl_identification_pin'),
            cssClass: 'user-pin-alert',
            inputs: [
                {
                    attributes: { inputmode: 'numeric' },
                    name: 'pin',
                    type: 'text',
                }
            ],
            buttons: [
                {
                    text: this.libraryService.getLabel('lbl_cancel'),
                    role: 'cancel'
                },
                {
                    text: this.libraryService.getLabel('lbl_accept')
                }
            ]
        });

        await alert.present();

        const alertInput = alert.querySelector('input');
        alertInput.addEventListener('beforeinput', (ev: InputEvent) => {
            const regex = /^\d$/;
            const key = ev.data;
            let pin = (ev.target as HTMLInputElement).value;

            if (ev.inputType.includes('deleteContent')) {
                return;
            }

            if (!regex.test(key) || pin.length >= 6) {
                ev.preventDefault();
                return;
            }

            pin = pin + key;

             if (pin.length === 6) {
                ev.preventDefault();
                alert.dismiss({ values: { pin } });
            }
        });

        const { data, role } = await alert.onDidDismiss();
        const pinRegex = /^\d{6}$/;

        if (role === 'cancel' || role === 'backdrop') {
            throw new CanceledPinError();
        }

        if (!(data?.values?.pin) || !pinRegex.test(data.values.pin)) {
            throw new IncorrectPinValueError();
        }

        return data.values.pin;
    }
}

class CanceledPinError extends Error {
    constructor() {
        super('Canceled pin operation');
    }
}

class IncorrectPinValueError extends Error {
    constructor() {
        super('Incorrect pin value');
    }
}
